import { Polygon } from 'geojson'
import { Map } from 'mapbox-gl'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import apiClient from '@app/config/axios.config'
import { RootState } from '@app/redux/store'
import { DrawArea, MapLayer } from '@app/redux/riskMap/riskMapSlice'
import { LegendLookup } from '@app/components/Molecules/Legend'
import { renderLayersInOrder } from '@app/components/Molecules/MapView/RiskMapView/Layers'
import { RegionOption } from '@app/components/Molecules/RegionFilter'
import { useMap } from '@app/contexts/ModuleMapContexts/RiskMapContext'
import { AddRegionMasksLayersProps } from '@app/contexts/ModuleMapContexts/RiskMapContext/RiskMapContext.types'
import { Snackbar, useSnackbars } from '@app/contexts/SnackbarContext'
import { MapLayerStyles } from '@app/contexts/ModuleMapContexts/GenericMapContext/GenericMapContext.types'
import {
  AssetHazardInstance,
  VulnerabilityData,
} from '@app/components/Molecules/MapView/RiskMapView/RiskMapView'
import {
  VisibleAssets,
  generateInformationLayerPalettes,
  getVisibleAssetIds,
  getVisibleInformationIds,
} from '@app/components/Molecules/MapView/RiskMapView/RiskMapView.utilities'
import { HazardOptionStateByHazard } from '@app/components/Molecules/RiskSideDrawerContent/tabs/InformativePanel/LayersTab/HazardControls/HazardControls'
import { IdentifyingInformationFilter } from '@app/types/filterTypes'

type FilterValuesType = Record<string, Record<string, string[] | null>> | null

export interface LayerContextInterface {
  prepareAndUpdateLayers: ({ layers }: PrepareAndUpdateLayersProps) => Promise<void>
  visibleAssets: VisibleAssets
  vulnerabilityData: VulnerabilityData
  elementFilters: IdentifyingInformationFilter
  filterKeyValues: FilterValuesType
  filterKeyValuesCache: { [key: string]: { [key: string]: string[] } } | null
  setElementFilters: React.Dispatch<React.SetStateAction<IdentifyingInformationFilter>>
  setfilterKeyValues: React.Dispatch<React.SetStateAction<FilterValuesType>>
  setFilterKeyValuesCache: React.Dispatch<
    React.SetStateAction<{
      [key: string]: { [key: string]: string[] }
    } | null>
  >
  layerLoading: boolean
  setLayerLoading: React.Dispatch<React.SetStateAction<boolean>>
}

export const LayerContext = createContext({} as LayerContextInterface)

export type ActiveHazardParameters = 'all' | 'onlyslr' | 'none'

export interface PrepareAndUpdateLayersProps {
  layers: MapLayer[]
  activeHazardParameters?: ActiveHazardParameters
  overwriteHazardParameters?: boolean
  regionMasks?: RegionOption[] | null
  drawAreas?: DrawArea[] | null
  applyVulnerabilityData?: boolean
}

export interface GetAndSetVisibleAssetsStateProps {
  map: Map
  assetLayers: MapLayer[]
  contextualLayers: MapLayer[]
  drawAreas: DrawArea[]
  regionMasks: RegionOption[] | null
  hasFilters: boolean
  elementFilters: IdentifyingInformationFilter
}

export interface RenderLayersProps {
  map: Map
  layers: MapLayer[]
  mapLayerStyles: MapLayerStyles
  visibleAssets: VisibleAssets
  vulnerabilityData: VulnerabilityData
  legendsData: LegendLookup
  regionMasks: RegionOption[] | null
  addRegionMasksLayers: (props: AddRegionMasksLayersProps) => void
  hasFilters: boolean
  applyVulnerabilityData: boolean
}

export function LayerProvider({ children }: { children: React.ReactNode }) {
  const {
    map,
    regionMasks,
    setRegionMasks,
    setHazardOptionState,
    cascadingAssetNDays,
    updateLayers,
    updateDrawAreas,
    addRegionMasksLayers,
    mapLayerStyles,
    removeRegionMasksLayers,
  } = useMap()
  const {
    drawAreas,
    legendsData,
    layers: reduxLayers,
  } = useSelector((state: RootState) => state.riskMap)

  const clientName = localStorage.getItem('client_name')

  const { displaySnackbar, removeSnackbar } = useSnackbars()

  /** FILTER BASED STATE */
  const [visibleAssets, setVisibleAssets] = useState<VisibleAssets>({})
  const [prevElementFilters, setPrevElementFilters] = useState<IdentifyingInformationFilter>({})
  const [elementFilters, setElementFilters] = useState<IdentifyingInformationFilter>({})
  const [filterKeyValues, setfilterKeyValues] = useState<FilterValuesType>(null)
  const [filterKeyValuesCache, setFilterKeyValuesCache] = useState<{
    [key: string]: { [key: string]: string[] }
  } | null>(null)
  const [prevDrawAreas, setPrevDrawAreas] = useState<DrawArea[] | null>(() => drawAreas)
  const [prevRegionKeys, setPrevRegionKeys] = useState<string>('')
  /** FILTER BASED STATE END*/

  const [vulnerabilityData, setVulnerabilityData] = useState<VulnerabilityData>({})
  const [layerLoading, setLayerLoading] = useState<boolean>(false)
  const cachedVulnData = useRef<{
    [key: string]: { [key: string]: AssetHazardInstance }
  }>({})
  // Records the exact filter state the last prepareAndUpdateLayers render
  // applied. The watcher effect below uses this to skip a redundant second
  // pass when a Redux filter change was caused by prepareAndUpdateLayers
  // itself (e.g. restoring a workspace) rather than a user action.
  const lastRenderedFiltersRef = useRef<{
    drawAreas: DrawArea[]
    regionKeys: string
    elementFiltersString: string
  } | null>(null)
  const tab = 'Risk'
  const isRiskStoryTab = tab !== 'Risk'
  /**
   * Responsible for rendering the layers and in order, this is what physically draws the layers to the map and gets their data
   * We want to call prepareAndUpdateLayers sparingly and using the least reactivity possible, we don't want multiple calls to it and thus rerenders of the same data
   */
  function renderLayers({
    map,
    layers,
    mapLayerStyles,
    visibleAssets,
    vulnerabilityData,
    legendsData,
    addRegionMasksLayers,
    regionMasks,
    hasFilters,
    applyVulnerabilityData,
  }: RenderLayersProps) {
    const informationLayerPalettes = generateInformationLayerPalettes(legendsData)
    renderLayersInOrder(
      map,
      layers,
      mapLayerStyles,
      visibleAssets,
      vulnerabilityData,
      legendsData,
      informationLayerPalettes,
      addRegionMasksLayers,
      regionMasks,
      hasFilters,
      applyVulnerabilityData,
    )
  }

  /**
   * Gets and sets the visible ids for asset and contextual layers
   * @TODO look at caching this, but be very careful since it's a huge payload and could make things worse
   */
  const getAndSetVisibleAssetsState = useCallback(
    async ({
      map,
      assetLayers,
      contextualLayers,
      drawAreas,
      regionMasks,
      hasFilters,
      elementFilters,
    }: GetAndSetVisibleAssetsStateProps): Promise<VisibleAssets> => {
      if (!hasFilters) {
        if (Object.keys(visibleAssets).length > 0) setVisibleAssets({})
        return {}
      }
      const [tempVisibleAssets, visibleInformationIds] = await Promise.all([
        getVisibleAssetIds(assetLayers, drawAreas, regionMasks, elementFilters),
        getVisibleInformationIds(contextualLayers, map, drawAreas),
      ])

      const combinedVisibleIds = { ...tempVisibleAssets, ...visibleInformationIds }
      setVisibleAssets(combinedVisibleIds)
      return combinedVisibleIds
    },
    [visibleAssets],
  )

  /**
   * Gets and sets the vulnerability stats for the asset and hazard layers
   * has room to be optimized via only fetching the data that hasn't been fetched yet
   * @returns Returns the vulnerability data for the asset and hazard layers
   */
  const getAndSetVulnerabilityStats = useCallback(
    async (assetLayers: MapLayer[], hazardLayers: MapLayer[]): Promise<VulnerabilityData> => {
      const newVulnerabilityData: VulnerabilityData = {}

      const fetchPromises: Promise<unknown>[] = []

      const regions = regionMasks ? regionMasks.map((region) => region.region).join(',') : ''
      const regionQuery = new URLSearchParams({ regions }).toString()

      const drawAreaCoordinateArray: Polygon | null =
        drawAreas.length > 0 ? (drawAreas[0].geometry as Polygon) : null
      const drawAreaCsv = drawAreaCoordinateArray
        ? drawAreaCoordinateArray.coordinates[0].flat().join(',')
        : ''

      for (const assetLayer of assetLayers) {
        const elementFilterCacheKey = JSON.stringify(elementFilters[assetLayer.type])
        for (const hazardLayer of hazardLayers) {
          const nDays = cascadingAssetNDays[assetLayer.type] ?? 0
          const nDaysQuery = new URLSearchParams({ n_days: nDays.toString() }).toString()
          const nDaysQueryForAsset = assetLayer.is_cascading ? nDaysQuery : ''

          const cache_key = `${assetLayer.type}-${hazardLayer.assetTag}-${regions}-${drawAreaCsv}-${nDaysQueryForAsset}-${elementFilterCacheKey}`
          if (cachedVulnData.current[cache_key]) {
            if (!newVulnerabilityData[assetLayer.type]) newVulnerabilityData[assetLayer.type] = {}
            newVulnerabilityData[assetLayer.type][hazardLayer.assetTag] =
              cachedVulnData.current[cache_key]
            continue
          }

          fetchPromises.push(
            apiClient
              .get(
                `/asset/risk/${assetLayer.type}/${
                  hazardLayer.assetTag
                }/${drawAreaCsv}?${regionQuery}${
                  nDaysQueryForAsset ? `&${nDaysQueryForAsset}` : ''
                }`,
              )
              .then((response) => {
                if (!newVulnerabilityData[assetLayer.type])
                  newVulnerabilityData[assetLayer.type] = {}
                newVulnerabilityData[assetLayer.type][hazardLayer.assetTag] = response.data
                cachedVulnData.current[cache_key] = response.data
              })
              .catch((error) => {
                // eslint-disable-next-line no-console
                console.error(error)
              }),
          )
        }
      }
      await Promise.all(fetchPromises)

      setVulnerabilityData(newVulnerabilityData)
      return newVulnerabilityData
    },
    [regionMasks, drawAreas, elementFilters, cascadingAssetNDays],
  )

  /**
   * This function gets the region masks and sets them if they are different from the current region masks
   * @param newRegionMasks - Optional new region masks to be considered
   * @returns Returns the region masks to be used
   */
  const getRegionMasks = useCallback(
    (newRegionMasks: RegionOption[] | null = null) => {
      if (newRegionMasks !== null && map) {
        if (newRegionMasks.length === 0) {
          if (regionMasks && regionMasks.length > 0) removeRegionMasksLayers({ map })
          return null
        } else {
          setRegionMasks(newRegionMasks)
          return newRegionMasks
        }
      }
      return regionMasks
    },
    [map, regionMasks, removeRegionMasksLayers, setRegionMasks],
  )

  /**
   * Mirrors getRegionMasks for drawn areas: when an explicit set of draw areas
   * is supplied (e.g. restoring a workspace) sync it to Redux and return it so
   * the very first visible-asset computation is already area-filtered, instead
   * of rendering the whole layer then re-filtering on the Redux change.
   * @param newDrawAreas - Optional draw areas to apply
   * @returns The draw areas to use for this render
   */
  const getDrawAreas = useCallback(
    (newDrawAreas: DrawArea[] | null = null) => {
      if (newDrawAreas !== null && map) {
        if (newDrawAreas !== drawAreas) {
          updateDrawAreas({ drawAreas: newDrawAreas })
        }
        return newDrawAreas
      }
      return drawAreas
    },
    [map, drawAreas, updateDrawAreas],
  )

  /**
   * This function takes all layers and controlled parameters and sets the hazard option state accordingly
   * @param layers - All layers to be considered
   * @param controlledParameters - For each relevant hazard id, lists the parameters that should be active for user input, as opposed to automatically set
   * @param overwriteHazardParameters - If true, will fully overwrite the hazard option state with the new state. If false, will merge the new state with the existing state, adding new options and removing options that are no longer relevant based on the layers, but keeping any existing options that are still relevant.
   */
  const setHazardOptionsForControlledParameters = useCallback(
    ({
      layers,
      activeHazardParameters,
      overwriteHazardParameters,
    }: {
      layers: MapLayer[]
      activeHazardParameters: ActiveHazardParameters
      overwriteHazardParameters: boolean
    }) => {
      const newHazardOptionState: HazardOptionStateByHazard = {}
      layers.forEach((layer) => {
        if (layer.layerType !== 'hazard') return
        if (layer.parameters) {
          Object.entries(layer.parameters).forEach(([paramKey, paramValue]) => {
            // only set the parameter if it's controlled
            if (layer.hazard_id === undefined) return
            if (
              activeHazardParameters === 'all' ||
              (activeHazardParameters === 'onlyslr' && paramKey === 'slr')
            ) {
              if (newHazardOptionState[layer.hazard_id] === undefined)
                newHazardOptionState[layer.hazard_id] = []
              if (!newHazardOptionState[layer.hazard_id].some((opt) => opt.key === paramKey))
                newHazardOptionState[layer.hazard_id].push({
                  key: paramKey,
                  value: paramValue,
                })
            }
          })
        }
      })
      if (overwriteHazardParameters) {
        // Fully overwrite the hazard option state with the new state
        setHazardOptionState((prevState) => {
          return {
            ...prevState,
            ...newHazardOptionState,
          }
        })
      } else {
        setHazardOptionState((prevState) => {
          const mergedState = { ...prevState }
          // Add new state options from newHazardOptionState
          Object.entries(newHazardOptionState).forEach(([hazardId, options]) => {
            if (!mergedState[hazardId]) {
              mergedState[hazardId] = options
            } else {
              options.forEach((option) => {
                if (!mergedState[hazardId].some((opt) => opt.key === option.key)) {
                  mergedState[hazardId].push(option)
                }
              })
            }
          })
          return mergedState
        })
      }
    },
    [setHazardOptionState],
  )

  /**
   * This function prepares all layers and considered, vuln, regions, polygons, visibility, and then executes the renderLayersInOrder function
   * This is the big controller of all things map layers
   * @param layers - All layers to be considered
   * @param activeHazardParameters - Default 'none'. Determines which hazard parameters should be actively set in the controls
   * @param overwriteHazardParameters - Default false. If true, will fully overwrite the hazard option state with the new state. If false, will merge the new state with the existing state, adding new options and removing options that are no longer relevant based on the layers, but keeping any existing options that are still relevant.
   * @param newRegionMasks - Optional new region masks to be considered
   */
  const prepareAndUpdateLayers = useCallback(
    async ({
      layers,
      activeHazardParameters = 'none',
      overwriteHazardParameters = false,
      regionMasks: newRegionMasks = null,
      drawAreas: newDrawAreas = null,
      applyVulnerabilityData = true,
    }: PrepareAndUpdateLayersProps) => {
      if (!map) return

      const regionMasksToUse = getRegionMasks(newRegionMasks)
      const drawAreasToUse = getDrawAreas(newDrawAreas)

      // Record the filter state THIS pass renders before any await. The
      // setRegionMasks / updateDrawAreas calls above queue state updates that
      // can flush (and fire the filter watcher effect) while we're awaiting
      // below; the watcher reads this ref to skip a redundant second pass, so
      // it must be set synchronously here, not after the await.
      lastRenderedFiltersRef.current = {
        drawAreas: drawAreasToUse,
        regionKeys: regionMasksToUse
          ? regionMasksToUse.map((region) => region.region).join(',')
          : '',
        elementFiltersString: JSON.stringify(elementFilters),
      }

      const hazardLayers = layers.filter((layer) => layer?.layerType === 'hazard')
      const assetLayers = layers.filter((layer) => layer?.layerType === 'asset')
      const contextualLayers = layers.filter((layer) => layer?.layerType === 'information')

      // Create empty VulnerabilityData by adding empty objects for each assetLayer type
      // when we don't want the vulnerability data to be applied.
      const vulnStatsGet: Promise<VulnerabilityData> = applyVulnerabilityData
        ? getAndSetVulnerabilityStats(assetLayers, hazardLayers)
        : Promise.resolve(Object.fromEntries(assetLayers.map((layer) => [layer.type, {}])))

      const nonEmptyElementFilters: IdentifyingInformationFilter = Object.fromEntries(
        Object.entries(elementFilters)
          .map(([layerType, filters]) => {
            // Remove filters with empty values arrays
            const nonEmptyFilters = Object.fromEntries(
              Object.entries(filters).filter(([_, fieldFilter]) => {
                return fieldFilter.values.length > 0
              }),
            )
            return [layerType, nonEmptyFilters]
          })
          .filter(([_, value]) => Object.keys(value).length > 0),
      )

      const hasFilters = !!(
        drawAreasToUse.length > 0 ||
        (regionMasksToUse && regionMasksToUse.length) ||
        Object.keys(nonEmptyElementFilters).length > 0
      )

      const visAssetsGet = getAndSetVisibleAssetsState({
        map,
        assetLayers,
        contextualLayers,
        drawAreas: drawAreasToUse,
        regionMasks: regionMasksToUse,
        hasFilters,
        elementFilters: nonEmptyElementFilters,
      })

      const loadingMessage: Snackbar = {
        message: 'Loading...',
        type: 'info',
      }

      const loadingTimeout = setTimeout(() => {
        displaySnackbar(loadingMessage)
      }, 1500)

      setHazardOptionsForControlledParameters({
        layers,
        activeHazardParameters,
        overwriteHazardParameters,
      })

      const res = await Promise.all([vulnStatsGet, visAssetsGet])
      let [vulnStats] = res
      const [, visAssets] = res
      vulnStats = vulnStats ?? vulnerabilityData

      clearTimeout(loadingTimeout)
      removeSnackbar(loadingMessage)

      // update the layers in the redux store
      updateLayers({ layers })

      // call function to draw layers on the map
      renderLayers({
        map,
        layers,
        mapLayerStyles,
        visibleAssets: visAssets,
        vulnerabilityData: vulnStats,
        legendsData,
        addRegionMasksLayers,
        regionMasks: regionMasksToUse,
        hasFilters,
        applyVulnerabilityData,
      })

      setLayerLoading(false)
    },
    [
      map,
      // drawAreas / regionMasks are intentionally not listed: this callback
      // only reads them via getDrawAreas / getRegionMasks, which already carry
      // them as deps, so the callback still updates when they change.
      getRegionMasks,
      getDrawAreas,
      getAndSetVulnerabilityStats,
      elementFilters,
      getAndSetVisibleAssetsState,
      setHazardOptionsForControlledParameters,
      vulnerabilityData,
      removeSnackbar,
      updateLayers,
      mapLayerStyles,
      legendsData,
      addRegionMasksLayers,
      displaySnackbar,
    ],
  )

  useEffect(() => {
    if (isRiskStoryTab) return
    // if the draw areas or region masks change, we need to re-render the layers
    // trying to avoid unnecessary renders though
    const elementFiltersString = JSON.stringify(elementFilters)
    const prevElementFiltersString = JSON.stringify(prevElementFilters)
    const regionKeys = regionMasks ? regionMasks.map((region) => region.region).join(',') : ''

    const changed =
      drawAreas !== prevDrawAreas ||
      regionKeys !== prevRegionKeys ||
      elementFiltersString !== prevElementFiltersString

    // If this exact filter state was just rendered by prepareAndUpdateLayers
    // (e.g. workspace restore set regions/draw areas via getRegionMasks /
    // getDrawAreas), re-rendering would be a no-op — skip the second pass.
    const last = lastRenderedFiltersRef.current
    const alreadyRendered =
      last !== null &&
      last.drawAreas === drawAreas &&
      last.regionKeys === regionKeys &&
      last.elementFiltersString === elementFiltersString

    if (changed && !alreadyRendered) {
      prepareAndUpdateLayers({ layers: reduxLayers })
    }
    setPrevDrawAreas(drawAreas)
    setPrevElementFilters(elementFilters)
    setPrevRegionKeys(regionKeys)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawAreas, regionMasks, elementFilters])

  useEffect(() => {
    prepareAndUpdateLayers({ layers: reduxLayers })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cascadingAssetNDays])

  useEffect(() => {
    setFilterKeyValuesCache({})
    cachedVulnData.current = {}
  }, [clientName])

  const contextValue = useMemo(
    () => ({
      prepareAndUpdateLayers,
      visibleAssets,
      vulnerabilityData,
      elementFilters,
      setElementFilters,
      filterKeyValues,
      setfilterKeyValues,
      filterKeyValuesCache,
      setFilterKeyValuesCache,
      layerLoading,
      setLayerLoading,
    }),
    [
      prepareAndUpdateLayers,
      visibleAssets,
      vulnerabilityData,
      elementFilters,
      filterKeyValues,
      filterKeyValuesCache,
      layerLoading,
      // Note: setter functions are stable and don't need to be in deps
    ],
  )

  return <LayerContext.Provider value={contextValue}>{children}</LayerContext.Provider>
}

export const useLayers = () => useContext(LayerContext)
